function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    // opening act.: a slider can be empty for a moment (e.g. cards filled in
    // later by script) - Dawn crashed here reading offsetLeft of undefined
    if (!element) return true;
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

/* opening act.: focus rings only for keyboard users. Closing the cart drawer
   with the X ringed the X and then - focus handed back - the cart icon in the
   header. html.oa-kbd is set by Tab/arrow keys and dropped again by any
   pointer or touch; base.css hides the rings of the header icons and drawer
   close buttons while it is absent. */
(function () {
  var root = document.documentElement;
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Tab' || e.key.indexOf('Arrow') === 0) root.classList.add('oa-kbd');
  }, true);
  ['pointerdown', 'touchstart'].forEach(function (t) {
    document.addEventListener(t, function () { root.classList.remove('oa-kbd'); }, { capture: true, passive: true });
  });
})();

/* opening act.: menu (left), search and cart never sit on top of each
   other. Tapping another of the three icons while one panel is out: the open
   panel folds in, the header STAYS white meanwhile (body.oa-hdr-hold, see
   sections/header.liquid), then the new panel folds out of it. Once
   everything is closed at the top of a transparent-header page, the header
   goes transparent again as usual. Done at the click level: the second step
   is a replayed click on the icon (or the cart's open()). A click that lands
   on the cart's page overlay right over an icon counts as a tap on it. */
(function () {
  var replaying = false;
  var body = document.body;
  function cart() { return document.querySelector('cart-drawer'); }
  function vis(el) { return el && el.getBoundingClientRect().width ? el : null; }
  function menuSummary() { return vis(document.querySelector('header-drawer details > summary')); }
  function searchModal() {
    var all = document.querySelectorAll('details-modal.header__search');
    for (var i = 0; i < all.length; i++) { if (vis(all[i].querySelector('summary'))) return all[i]; }
    return null;
  }
  function openPanel() {
    var s = menuSummary();
    if (s && s.parentNode.hasAttribute('open')) return 'menu';
    var c = cart();
    if (c && c.classList.contains('active')) return 'cart';
    var dm = searchModal();
    if (dm && dm.querySelector('details[open]')) return 'search';
    return null;
  }
  function over(el, e) {
    if (!el || e.clientX == null) return false;
    var r = el.getBoundingClientRect();
    return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
  }
  function hit(el, e) {
    return el && (el.contains(e.target) || (e.target.id === 'CartDrawer-Overlay' && over(el, e)));
  }
  function replay(el) { replaying = true; el.click(); replaying = false; }
  function closePanel(which) {
    if (which === 'menu') { replay(menuSummary()); return 450; }
    if (which === 'cart') { cart().close(); return 460; }
    if (which === 'search') { var dm = searchModal(); if (dm) dm.close(); return 330; }
    return 0;
  }
  function openTarget(which, el) {
    if (which === 'cart') { var c = cart(); var b = document.getElementById('cart-icon-bubble'); c.open(b); return; }
    replay(el);
  }
  var holdT = null;
  document.addEventListener('click', function (e) {
    if (replaying) return;
    var cur = openPanel();
    if (!cur) return;
    var ms = menuSummary();
    var dm = searchModal();
    var ss = dm && dm.querySelector('summary');
    var bubble = document.getElementById('cart-icon-bubble');
    var target = null, el = null;
    if (hit(ms, e)) { target = 'menu'; el = ms; }
    else if (hit(ss, e)) { target = 'search'; el = ss; }
    else if (hit(bubble, e) && cart()) { target = 'cart'; el = bubble; }
    if (!target || target === cur) return; // same icon: its own toggle closes it
    e.preventDefault();
    e.stopImmediatePropagation();
    clearTimeout(holdT);
    body.classList.add('oa-hdr-hold');
    var wait = closePanel(cur);
    setTimeout(function () {
      openTarget(target, el);
      // the new panel keeps the header white from here on
      holdT = setTimeout(function () { body.classList.remove('oa-hdr-hold'); }, 80);
    }, wait);
  }, true);
})();

/* opening act.: standing banner photos on phones (base.css .animate--fixed,
   oa-mb.css .oa-mb-fb). The banner at the very top of the page is marked
   (.oa-fx-first) so its photo may reach up behind the status bar. Each
   banner's media box reaches up to one screen (+12rem) below the banner so
   the sticky photo can stand still - but never past the end of the page
   (--oa-fx-below), or the page would get longer. */
(function () {
  var raf = 0;
  function place() {
    raf = 0;
    if (window.innerWidth > 749) return;
    var y = window.scrollY;
    var list = Array.prototype.slice.call(document.querySelectorAll('.banner > .animate--fixed, .oa-mb-fb'));
    if (!list.length) return;
    // the page's real end, without the media boxes' own overhang
    list.forEach(function (m) { m.style.setProperty('--oa-fx-below', '0px'); });
    var end = document.documentElement.scrollHeight;
    var rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    var lvh = Math.max(window.innerHeight, (window.visualViewport && window.visualViewport.height) || 0, screen.height || 0);
    list.forEach(function (m) {
      var bn = m.classList.contains('oa-mb-fb') ? m : (m.closest('.banner') || m);
      var r = bn.getBoundingClientRect();
      if (!r.height) return;
      var top = Math.round(r.top + y), bottom = Math.round(r.bottom + y);
      m.classList.toggle('oa-fx-first', top < 2);
      var below = Math.max(0, Math.min(lvh + 12 * rem, end - bottom));
      m.style.setProperty('--oa-fx-below', below + 'px');
    });
  }
  function queue() { if (!raf) raf = requestAnimationFrame(place); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', queue, { once: true });
  else queue();
  window.addEventListener('load', queue);
  var lastW = window.innerWidth;
  window.addEventListener('resize', function () { if (window.innerWidth !== lastW) { lastW = window.innerWidth; queue(); } });
  if (window.ResizeObserver) {
    // the page's content and its end (footer below <main>) may still grow
    var ro = new ResizeObserver(queue);
    var main = document.getElementById('MainContent');
    if (main) ro.observe(main);
    if (document.body) ro.observe(document.body);
  }
})();

/* opening act.: header white + a panel folding out of it as ONE sheet, on one
   timeline (Web Animations). The white's lower edge runs down through the
   header and straight on into the panel - and back up on closing - so the
   speed never jumps where header and panel meet. Used by the menu (below),
   the search (snippets/header-search.liquid) and the cart's opening
   (snippets/cart-drawer.liquid).
   Header already white (scrolled, or switching panels): the panel runs alone
   on the same curve. Either way the host's inverted corner fillets grow and
   shrink with the panel's edge instead of being cut square.
   Returns false when it cannot run (no pseudo-element animations, reduced
   motion) - the CSS sequence stays. */
(function () {
  var can = (function () {
    try { return typeof KeyframeEffect !== 'undefined' && 'pseudoElement' in KeyframeEffect.prototype; } catch (e) { return false; }
  })();
  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function bez(x1, y1, x2, y2) {
    function c(a1, a2, t) { return ((1 - 3 * a2 + 3 * a1) * t + (3 * a2 - 6 * a1)) * t * t + 3 * a1 * t; }
    return function (x) {
      var lo = 0, hi = 1, t = x;
      for (var i = 0; i < 24; i++) { t = (lo + hi) / 2; if (c(x1, x2, t) < x) lo = t; else hi = t; }
      return c(y1, y2, t);
    };
  }
  function inv(E, y) {
    var lo = 0, hi = 1;
    for (var i = 0; i < 30; i++) { var m = (lo + hi) / 2; if (E(m) < y) lo = m; else hi = m; }
    return hi;
  }
  /* One motion for every panel hanging from the header. Opening leaves at
     once (the tap answers immediately) and settles long and soft; closing
     gathers speed and eases out at the header's top edge. */
  var EASE_OPEN = bez(0.45, 0.05, 0.15, 1);
  var EASE_CLOSE = bez(0.55, 0, 0.25, 1);
  function duration(H, P, reverse) {
    var T = Math.min(640, Math.max(460, 380 + 0.35 * (H + P)));
    return reverse ? T * 0.8 : T;
  }
  var wipes = 0; // live runs that drive the header's white

  window.oaSheet = function (panel, reverse, host, done, opts) {
    opts = opts || {};
    var sec = document.querySelector('.section-header');
    var hw = sec && sec.querySelector('.header-wrapper');
    if (!can || !hw || !panel || (reduce && reduce.matches)) return false;
    var withHeader =
      document.documentElement.classList.contains('oa-hdr-top') &&
      !document.body.classList.contains('oa-hdr-hold');
    var H = withHeader ? hw.offsetHeight : 0;
    var P = panel.offsetHeight;
    if (!P) return false;
    var rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    var S = rem * 5;   // the panel's soft shadow (up to 5rem below it) is part of the run
    var F = rem * 1.6; // fillet size
    var D = H + P + S;
    // Tapped again mid-way: carry on from where the edge is - no jump.
    var d0 = reverse ? D : 0;
    var prev = panel.__oaRun;
    if (prev && prev.live) {
      var pp = prev.pos();
      d0 = pp.q > 0 ? H + pp.q : (H ? Math.min(H, pp.h) : 0);
      prev.stop();
    }
    var d1 = reverse ? 0 : D;
    var span = Math.abs(d1 - d0);
    if (span < 1) { if (done) done(); return true; }
    var E = reverse ? EASE_CLOSE : EASE_OPEN;
    var T = duration(H, P, reverse) * Math.max(0.35, span / D);
    // The menu's <details> closes after 400ms (Dawn): the panel part of the
    // run must be in by then.
    if (reverse && opts.maxPanel && d0 > H) {
      var tp = inv(E, 1 - H / d0);
      if (tp * T > opts.maxPanel) T = opts.maxPanel / tp;
    }
    // Panel alone folding in while the header stays white (switching to
    // another panel): short and quick, the next one is waiting.
    if (reverse && !withHeader) T = Math.min(T, 320);
    T = Math.round(T);
    // Bottom insets as calc(100% - visible px): right even if the panel's
    // height changes while it runs (menu leaving a submenu, cart re-measured).
    var kh = [], kp = [], kf = [], N = 48;
    for (var i = 0; i <= N; i++) {
      var t = i / N, d = d0 + (d1 - d0) * E(t);
      var h = Math.min(H, d), q = Math.max(0, d - H);
      kh.push({ offset: t, opacity: 1, clipPath: 'inset(0px 0px calc(100% - ' + h.toFixed(2) + 'px) 0px)' });
      kp.push({
        offset: t,
        clipPath: i === N && !reverse ? 'inset(0px -2rem -5rem -2rem)' : 'inset(0px -2rem calc(100% - ' + q.toFixed(2) + 'px) -2rem)'
      });
      // While less than one fillet-height of panel is below the header the
      // fillet SHRINKS with it (drawn at the panel's corner, curve radius =
      // visible panel) instead of being cut - cut, its square top showed as a
      // corner. Animated as background-size, NOT transform: Safari runs
      // transform animations on the GPU but clip-path on the main thread, so
      // a busy moment let fillet and panel edge drift apart - the corners.
      var fs = Math.min(F, q).toFixed(2) + 'px';
      kf.push({ offset: t, backgroundSize: fs + ' ' + fs });
    }
    var run = { live: true };
    panel.__oaRun = run;
    if (withHeader) { wipes++; sec.classList.add('oa-hdr-js-wipe'); }
    if (host) host.classList.add('oa-sheet-js');
    var o = { duration: T, fill: 'both', easing: 'linear' };
    var ah = withHeader ? hw.animate(kh, { duration: T, fill: 'both', easing: 'linear', pseudoElement: '::before' }) : null;
    var ap = panel.animate(kp, o);
    var af = [];
    if (host) {
      (opts.fillets || ['::before', '::after']).forEach(function (pe) {
        try { af.push(host.animate(kf, { duration: T, fill: 'both', easing: 'linear', pseudoElement: pe })); } catch (e) {}
      });
    }
    // Opening: a long first frame (iPhone raising the keyboard for the
    // search field, a busy main thread) let the clock run on unseen - the
    // first frame painted showed the panel already (almost) out: it "jumped"
    // down. Hold the clock back by the time lost in such a gap, so the visible
    // movement always starts from the top. Only the first few frames.
    if (!reverse) {
      var lastF = performance.now(), nF = 0;
      var anims = [ah, ap].concat(af);
      var watch = function (ts) {
        // (the whole opening, not only the first frames: the first search
        // after loading the page raised the keyboard mid-way - a stall there
        // made the panel jump the rest of the way at once)
        if (!run.live || nF++ > 90) return;
        var gap = ts - lastF;
        if (gap > 50) {
          var back = gap - 16;
          anims.forEach(function (a) {
            if (a) a.currentTime = Math.max(0, (a.currentTime || 0) - back);
          });
        }
        lastF = ts;
        requestAnimationFrame(watch);
      };
      requestAnimationFrame(watch);
    }
    function release() {
      if (withHeader && --wipes <= 0) { wipes = 0; sec.classList.remove('oa-hdr-js-wipe'); }
    }
    run.pos = function () {
      var tt = Math.min(1, Math.max(0, (ap.currentTime || 0) / T));
      var dd = d0 + (d1 - d0) * E(tt);
      return { h: Math.min(H, dd), q: Math.max(0, dd - H) };
    };
    run.stop = function () {
      run.live = false;
      if (ah) ah.cancel();
      ap.cancel();
      af.forEach(function (a) { a.cancel(); });
      release();
    };
    ap.onfinish = function () {
      if (!run.live) return;
      run.live = false;
      if (done) done();
      // Folding back in: the header's white is already gone, but the CSS
      // "closing" state (body.oa-cart-closing, .oa-hdr-sheet-closing) still
      // says white for a moment. Letting go of the animation then made the
      // header flash white and wipe up a second time (phone cart, at the top).
      // So hold the animation's end state until that state is over.
      var holdHdr = reverse && ah;
      if (holdHdr) {
        var t0 = Date.now();
        var letGo = function () {
          var busy =
            sec.classList.contains('oa-hdr-sheet-closing') ||
            document.body.classList.contains('oa-cart-closing');
          if (busy && Date.now() - t0 < 1200) { setTimeout(letGo, 50); return; }
          ah.cancel();
          requestAnimationFrame(release);
        };
        // First look only after the current task: done() above may just have
        // closed the panel (search), and the header notices that in a
        // microtask (class observer) - checked at once, the "closing" state
        // wasn't set yet, the hold let go too early and Safari replayed the
        // white from the open state (white flash after closing the search).
        setTimeout(letGo, 0);
      }
      requestAnimationFrame(function () {
        if (panel.__oaRun !== run) return; // a newer run took over
        if (ah && !holdHdr) ah.cancel();
        ap.cancel();
        requestAnimationFrame(function () {
          if (panel.__oaRun !== run) return;
          if (host) host.classList.remove('oa-sheet-js');
          if (!holdHdr) release();
          // The fillets let go only together with their class: cancelled one
          // frame earlier they snapped back to full size while still forced
          // visible - the corner that flashed at the end of closing. After
          // CLOSING they hold their zero size a while longer: the cart's
          // fillets fade out with a delay (component-cart-drawer.css) and
          // would stand there whole meanwhile.
          if (reverse) setTimeout(function () { af.forEach(function (a) { a.cancel(); }); }, 600);
          else af.forEach(function (a) { a.cancel(); });
          panel.__oaRun = null;
        });
      });
    };
    return true;
  };
})();

/* opening act.: the phone menu runs on the same sheet as search and cart -
   opening and folding back in. Before, header white and panel were two CSS
   transitions one after the other, and the speed jumped where they met. */
(function () {
  var mq = window.matchMedia('(max-width: 989px)');
  function start(details, reverse) {
    var drawer = details.querySelector('#menu-drawer');
    if (!drawer || !mq.matches || !window.oaSheet) return;
    window.oaSheet(drawer, reverse, drawer, null, reverse ? { maxPanel: 370 } : null);
  }
  function hook() {
    var details = document.getElementById('Details-menu-drawer-container');
    if (!details || details.__oaSheetHook) return;
    details.__oaSheetHook = true;
    // Opening: the moment <details> opens (before the next paint, before
    // Dawn adds .menu-opening) the sheet takes over header white and panel.
    var wasOpen = details.hasAttribute('open');
    new MutationObserver(function () {
      var open = details.hasAttribute('open');
      if (open && !wasOpen) start(details, false);
      wasOpen = open;
    }).observe(details, { attributes: true, attributeFilter: ['open'] });
  }
  customElements.whenDefined('header-drawer').then(function () {
    var C = customElements.get('header-drawer');
    if (C && !C.prototype.__oaSheetClose) {
      C.prototype.__oaSheetClose = true;
      var orig = C.prototype.closeMenuDrawer;
      // Closing: set up BEFORE Dawn drops .menu-opening and moves focus (a
      // focus change makes Safari start the CSS transition at once).
      C.prototype.closeMenuDrawer = function (event, elementToFocus) {
        var d = this.mainDetailsToggle;
        if (event !== undefined && elementToFocus && d && d.hasAttribute('open') && d.classList.contains('menu-opening')) {
          start(d, true);
        }
        return orig.apply(this, arguments);
      };
    }
    hook();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hook, { once: true });
  else hook();

  /* Filter & sort sheet on phones (snippets/facets.liquid): the same sheet.
     Panel = .mobile-facets__inner; its fillet is the form's ::after (left,
     on the fixed layer) - the form's ::before is the haze, left alone. The
     form is re-rendered by the filters, so the hooks are delegated. */
  var phone = window.matchMedia('(max-width: 749px)');
  function facets(details, reverse) {
    var form = details.querySelector('.mobile-facets');
    var inner = form && form.querySelector('.mobile-facets__inner');
    if (!inner || !phone.matches || !window.oaSheet) return;
    window.oaSheet(inner, reverse, form, null, reverse ? { maxPanel: 370, fillets: ['::after'] } : { fillets: ['::after'] });
  }
  new MutationObserver(function (recs) {
    recs.forEach(function (r) {
      var d = r.target;
      if (!d.classList || !d.classList.contains('mobile-facets__disclosure')) return;
      var open = d.hasAttribute('open');
      if (open && !d.__oaWasOpen) facets(d, false);
      d.__oaWasOpen = open;
    });
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
  customElements.whenDefined('menu-drawer').then(function () {
    var C = customElements.get('menu-drawer');
    if (!C || C.prototype.__oaFacetsSheet) return;
    C.prototype.__oaFacetsSheet = true;
    var orig = C.prototype.closeMenuDrawer;
    C.prototype.closeMenuDrawer = function (event) {
      var d = this.mainDetailsToggle;
      if (event !== undefined && d && d.classList.contains('mobile-facets__disclosure') &&
          d.hasAttribute('open') && d.classList.contains('menu-opening')) {
        facets(d, true);
      }
      return orig.apply(this, arguments);
    };
  });
})();

/* opening act.: the cart's closing sheet must be set up BEFORE close() removes
   .active - close() moves focus (removeTrapFocus), which makes Safari compute
   styles at once, so the panel's own CSS transition had already started by
   the time the class observer (snippets/cart-drawer.liquid) set the sheet up.
   Transitions outrank animations: the panel folded on its CSS curve while
   header and corner fillets followed the sheet - a corner showed at the end. */
customElements.whenDefined('cart-drawer').then(function () {
  var C = customElements.get('cart-drawer');
  if (!C || C.prototype.__oaSheetClose) return;
  C.prototype.__oaSheetClose = true;
  var orig = C.prototype.close;
  C.prototype.close = function () {
    // Closing runs like the menu: the panel's own CSS fold (corner fillets
    // stay until it is in), then the header's white follows (header.liquid).
    // Safari let the panel's CSS transition win over a scripted closing sheet,
    // so panel and fillets ran on two clocks - the square corner at the end.
    this.__oaWasOn = false;
    // Header white picks up the panel's speed where the panel arrives (its
    // fold ends fast: ease-in, end slope 1.8) instead of starting slower -
    // that drop in speed read as a hitch between cart and header. Duration
    // from the panel height: start slope 6 of the header curve = same px/s.
    var sec = document.querySelector('.section-header');
    var inner = this.querySelector('.drawer__inner');
    // Phones: back in on the same sheet as the opening (and as menu and
    // search) - set up BEFORE close() drops .active, so no CSS transition
    // can start first. The panel part is in before the drawer hides (0.45s).
    if (inner && this.classList.contains('active') && window.innerWidth < 990 && window.oaSheet &&
        window.oaSheet(inner, true, this, null, { maxPanel: 400 })) {
      return orig.apply(this, arguments);
    }
    if (sec && inner && this.classList.contains('active') && document.documentElement.classList.contains('oa-hdr-top')) {
      var H = Math.max(1, sec.getBoundingClientRect().bottom);
      var P = inner.getBoundingClientRect().height + 50; // + shadow margin of the clip
      var v = 1.8 * P / 0.4;                              // px/s at arrival
      var d = Math.min(0.22, Math.max(0.08, 6 * H / v));
      sec.style.setProperty('--oa-sheet-close-hd', d.toFixed(3) + 's');
      sec.style.setProperty('--oa-sheet-close-hease', 'cubic-bezier(0.1, 0.6, 0.4, 1)');
      clearTimeout(sec.__oaHdT);
      sec.__oaHdT = setTimeout(function () {
        sec.style.removeProperty('--oa-sheet-close-hd');
        sec.style.removeProperty('--oa-sheet-close-hease');
      }, 900);
    }
    return orig.apply(this, arguments);
  };
  // The page overlay was bound to the ORIGINAL close() when the cart was
  // built - route it through the patched one.
  document.querySelectorAll('cart-drawer').forEach(function (c) {
    var ov = c.querySelector('#CartDrawer-Overlay');
    if (!ov) return;
    c.addEventListener('click', function (e) {
      if (e.target !== ov) return;
      e.stopPropagation();
      c.close();
    }, true);
  });
});
